import { DefaultPopover } from '@/core/popover/DefaultPopover.tsx'
import { useOptionsPopover } from '@/core/popover/popover-hooks.tsx'
import { useOnDispose } from '@/common/react/react-hooks'
import type { NewItemButtonController } from './NewItemButtonController'
import { NewItemTypePopoverItems } from './NewItemPopoverItem.tsx'
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined'
import { Box, IconButton } from '@mui/material'
import './SelectedSpaceHeader.css'
import { iconButtonSx } from '@/common/react/ui/IconButton'
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined'
import CreateObjectPage from '../../../objects/create-object-page/CreateObjectPage.tsx'
import { useTransientDiRef } from '@/common/react/di-hooks'
import { useBehaviorObservable } from '@/common/react/observable-hooks'
import { useEffect } from 'react'
import type { ObjectTypeModel } from '@/core/gql/gql-models-override.ts'

export function NewItemButton() {
  const controller = useTransientDiRef<NewItemButtonController>('NewItemButtonController')
  const typePopover = useOptionsPopover<ObjectTypeModel>()
  const newObjectPopover = useOptionsPopover()
  const selectedType = useBehaviorObservable(controller.lastSelectedType$)

  useEffect(() => {
    controller.init()
    return () => controller.dispose()
  }, [])

  const isEnabled = !!selectedType
  function onNewItemClicked(e: React.MouseEvent) {
    newObjectPopover.openPopover(e.currentTarget)
  }

  const fontSize = 24
  function containerShadow(isLight: boolean) {
    const color = isLight ? `0,0,0` : `255,255,255`
    const coef = isLight ? 0.3 : 1
    return `
              ${isLight ? '' : `0 0 0 1px rgba(${color},${0.1 * coef}),`}
              0 0 0 2px rgba(${color},${0.06 * coef}),
              0 0px 6px rgba(${color},${0.4 * coef}),
              0 0px 10px rgba(${color},${0.1 * coef})
            `
  }

  return (
    <Box
      className="selectedSpaceHeaderBlock"
      sx={{
        py: 0,
        px: 0,
        borderRadius: 1,
        backgroundColor: (theme) => theme.palette.background.default,
        boxShadow: (theme) => containerShadow(theme.palette.mode === 'light'),
      }}
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      <IconButton disabled={!isEnabled} onClick={onNewItemClicked} sx={iconButtonSx}>
        <AddBoxOutlinedIcon sx={{ fontSize }} />
      </IconButton>

      <IconButton
        disabled={!isEnabled}
        onClick={(e) => {
          typePopover.openPopover(e.currentTarget)
        }}
        sx={{
          ...iconButtonSx,
          px: 0,
          // py: 2,
        }}
      >
        <ExpandMoreOutlinedIcon sx={{ fontSize, transform: 'scaleX(1.0) scaleY(1.4)' }} />
      </IconButton>
      <DefaultPopover {...{ anchorEl: typePopover.anchorEl!, close: typePopover.closePopover }}>
        <NewItemTypePopoverItems setPopoverResult={typePopover.setResult} controller={controller} />
      </DefaultPopover>

      {selectedType && (
        <DefaultPopover
          {...{ anchorEl: newObjectPopover.anchorEl!, close: newObjectPopover.closePopover }}
        >
          <CreateObjectPage
            key={newObjectPopover.anchorEl ? 'open' : 'closed'}
            type={selectedType}
            closePopover={newObjectPopover.closePopover}
          />
        </DefaultPopover>
      )}
    </Box>
  )
}
